import React, { useState, useEffect, useMemo } from 'react'

import { renderMuiFormField } from 'utils/forms/renderMuiFormField'
import { selectData } from 'layouts/onboarding'
import FormField from 'components/FormField'
import ValuePoint from 'admin/components/ValuePoint'

// MUI Imports
import { Card, Grid, Typography, Box, Collapse } from '@mui/material'
import { ExpandMore } from '@mui/icons-material'

import {
  format,
  addDays,
  max,
  addYears,
  differenceInYears,
  differenceInMonths,
  addMonths,
} from 'date-fns'

const dollarFormat = Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
})

const isValidDate = (d) =>
  new Date(d) instanceof Date && !Number.isNaN(new Date(d).getTime())

const dateToYears = (startDate, endDate) => {
  const years =
    startDate && endDate
      ? differenceInYears(new Date(endDate), new Date(startDate))
      : ''
  const diff =
    startDate && endDate
      ? differenceInMonths(new Date(endDate), new Date(startDate))
      : ''
  const months =
    startDate && endDate
      ? differenceInMonths(
          new Date(endDate),
          addMonths(new Date(startDate), years * 12)
        )
      : ''
  return { years, months }
}

function Employment({
  collapseSections,
  toggleCollapse,
  firstFullyEligibleDate,
  atRetirementFERSSupplement,
  intakeState,
  updateIntakeStateAtKey,
  updateIntakeStateAndSubmit,
}) {
  const isGovernmentEmployee = intakeState?.governmentEmployee
  const [localForm, setLocalForm] = useState({
    positionTitle: intakeState.positionTitle || '',
    dateOfHire: intakeState.dateOfHire || '',
    dateOfPlannedRetirement: intakeState.dateOfPlannedRetirement || '',
  })
  const handleChange = (field) => (value) => {
    setLocalForm((prev) => ({ ...prev, [field]: value }))
  }
  const FIXED_HEIGHT = 24
  const [logoWidth, setLogoWidth] = useState(0) // default fallback

  const logoUrl = useMemo(
    () =>
      selectData.agency.find(({ value }) => value === intakeState?.agency)
        ?.logo || '',
    [intakeState?.agency]
  )
  useEffect(() => {
    if (!logoUrl) return

    const img = new Image()
    img.onload = () => {
      const { naturalWidth, naturalHeight } = img
      const scaledWidth = (naturalWidth / naturalHeight) * FIXED_HEIGHT
      setLogoWidth(scaledWidth)
    }
    img.src = logoUrl
  }, [logoUrl])
  const agencyLogoStyle = useMemo(
    () => ({
      '& [class$="container"]>div:first-of-type>div:first-of-type>div:first-of-type':
        {
          display: 'flex',
        },
      '& [class$="container"]>div:first-of-type>div:first-of-type>div:first-of-type::before':
        {
          content: '""',
          display: 'inline-block',
          height: `${FIXED_HEIGHT}px`,
          width: `${logoWidth}px`,
          marginRight: '8px',
          marginTop: '2px',
          backgroundImage: `url("${logoUrl}")`,
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
        },
    }),
    [logoUrl, logoWidth]
  )
  const clientAge =
    dateToYears(intakeState?.dateOfBirth || new Date(), new Date())?.years || 0

  // adding in CYS calcs so we don't have to rely on dashboard calcs in this section
  const eligibilityScenariosActualCYSToday =
    differenceInMonths(new Date(), new Date(intakeState.dateOfHire)) / 12 +
    (intakeState?.militaryActiveDutyPension
      ? 0
      : intakeState?.militaryPaidDeposit
      ? 0
      : differenceInMonths(
          new Date(intakeState?.militaryActiveDutyEndDate || new Date()),
          new Date(intakeState?.militaryActiveDutyStartDate || new Date())
        ) / 12)
  const FERSYearsOfService =
    differenceInMonths(
      new Date(intakeState?.dateOfPlannedRetirement || new Date()),
      new Date(intakeState?.dateOfHire || new Date())
    ) /
      12 +
    (intakeState?.militaryActiveDutyPension
      ? 0
      : intakeState?.militaryPaidDeposit
      ? 0
      : differenceInMonths(
          new Date(intakeState?.militaryActiveDutyEndDate || new Date()),
          new Date(intakeState?.militaryActiveDutyStartDate || new Date())
        ) / 12)
  const sickLeaveMonths =
    Math.floor(
      (intakeState?.plannedSickLeave
        ? intakeState?.plannedSickLeave
        : intakeState?.currentSickLeaveBalance || 0) / 174
    ) / 12
  const creditableYearsOfServiceAtRetirement =
    FERSYearsOfService + sickLeaveMonths
  const creditableYearsOfServiceToday =
    eligibilityScenariosActualCYSToday + sickLeaveMonths

  return (
    <>
      <Grid item xs={12} md={4} xl={3}>
        <Card
          sx={{
            overflow: 'visible',
            textAlign: 'left',
            boxShadow: '0px 4px 25px -8px rgba(0,0,0,0.2)',
            backgroundColor: '#f8f9fa',
            borderRadius: 2,
            p: 2,
          }}
        >
          <Grid container columnSpacing={3} rowSpacing={2}>
            <Grid item xs={12}>
              <Typography variant="valueTitle">VALUE POINTS</Typography>
              <Collapse
                in={collapseSections.employment}
                style={{ width: '100%' }}
              >
                <ValuePoint text="Strategy - Best day of the month to retire" />
                {clientAge < 62 && (
                  <ValuePoint text="Strategy - Make note of Supplement" />
                )}
                {firstFullyEligibleDate < new Date() ? (
                  <ValuePoint
                    text="Fully Eligible To Retire NOW"
                    type="success"
                  />
                ) : (
                  <ValuePoint
                    text={`First Fully Eligible Date: ${format(
                      firstFullyEligibleDate === undefined
                        ? new Date()
                        : firstFullyEligibleDate,
                      'MM/dd/yyyy'
                    )}`}
                  />
                )}
                <ValuePoint
                  text={`62 + 20 "Booster" Date: ${
                    isValidDate(intakeState?.dateOfHire) &&
                    isValidDate(intakeState?.dateOfBirth)
                      ? format(
                          addDays(
                            max([
                              addYears(new Date(intakeState?.dateOfHire), 20),
                              addYears(new Date(intakeState?.dateOfBirth), 62),
                            ]),
                            1
                          ),
                          'MM/dd/yyyy'
                        )
                      : ''
                  }`}
                  type="donut"
                />
                {max([
                  addYears(new Date(intakeState?.dateOfHire), 20),
                  addYears(new Date(intakeState?.dateOfBirth), 62),
                ]) <= new Date(intakeState?.dateOfPlannedRetirement) ? (
                  <ValuePoint text='Will get 10% "Booster"' type="success" />
                ) : (
                  <ValuePoint text='Will NOT get 10% "Booster"' type="error" />
                )}
                {clientAge < 62 && atRetirementFERSSupplement && (
                  <ValuePoint
                    text={`Supplement Value: ${dollarFormat.format(
                      atRetirementFERSSupplement || 0
                    )}`}
                    type="data"
                  />
                )}
              </Collapse>
            </Grid>
          </Grid>
        </Card>
      </Grid>
      <Grid item xs={12} md={8} xl={9}>
        <Card
          id="employment"
          sx={{
            overflow: 'visible',
            textAlign: 'left',
            boxShadow: '0px 4px 25px -8px rgba(0,0,0,0.2)',
            borderRadius: 2,
            p: 2,
            pb: 0,
          }}
        >
          <Grid container columnSpacing={3} rowSpacing={2} pb={2}>
            <Grid
              item
              xs={12}
              onClick={() => toggleCollapse()}
              sx={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Typography variant="h4" sx={{ flexGrow: 1 }}>
                Employment
              </Typography>
              <ExpandMore
                sx={{
                  height: '1.5em',
                  width: '1.5em',
                  transform: collapseSections.employment
                    ? 'rotate(180deg)'
                    : 'rotate(0deg)',
                  transition: 'transform 0.3s ease-in-out',
                }}
              />
            </Grid>
          </Grid>
          <Collapse in={collapseSections.employment} style={{ width: '100%' }}>
            <Grid container columnSpacing={3} rowSpacing={2} pb={2}>
              <Grid item xs={6} lg={3}>
                {renderMuiFormField({
                  autoFocus: false,
                  id: 'employmentStatus',
                  label: 'Employment Status',
                  placeholder: '',
                  inputType: 'select',
                  value: intakeState.employmentStatus,
                  onChange: updateIntakeStateAndSubmit((employmentStatus) =>
                    updateIntakeStateAtKey({ employmentStatus })
                  ),
                  options: selectData.employmentStatus,
                })}
              </Grid>
              <Grid item xs={6} lg={3}>
                {renderMuiFormField({
                  autoFocus: false,
                  id: 'governmentEmployee',
                  label: 'Government Employee?',
                  placeholder: '',
                  inputType: 'select',
                  value: intakeState.governmentEmployee,
                  onChange: updateIntakeStateAndSubmit((governmentEmployee) =>
                    updateIntakeStateAtKey({ governmentEmployee })
                  ),
                  options: selectData.yesNo,
                })}
              </Grid>
              {isGovernmentEmployee && (
                <Grid item xs={12} md={6} lg={6} sx={agencyLogoStyle}>
                  {renderMuiFormField({
                    autoFocus: false,
                    id: 'agency',
                    label: 'Federal Agency',
                    placeholder: '',
                    inputType: 'select',
                    value: intakeState.agency,
                    searchable: true,
                    onChange: updateIntakeStateAndSubmit((agency) =>
                      updateIntakeStateAtKey({ agency })
                    ),
                    options: selectData.agency,
                  })}
                </Grid>
              )}
              <Grid item xs={12} md={6} lg={4}>
                {renderMuiFormField({
                  autoFocus: false,
                  id: 'positionTitle',
                  label: 'Position/Title',
                  placeholder: 'Position/Title',
                  inputType: 'text',
                  value: localForm.positionTitle,
                  onChange: (e) => handleChange('positionTitle')(e),
                  onBlur: updateIntakeStateAndSubmit((positionTitle) =>
                    updateIntakeStateAtKey({ positionTitle })
                  ),
                })}
              </Grid>
              {isGovernmentEmployee && (
                <Grid item xs={6} md={6} lg={4}>
                  {renderMuiFormField({
                    autoFocus: false,
                    id: 'dateOfHire',
                    label: 'Service Computation Date',
                    placeholder: '01/01/1970',
                    inputType: 'date',
                    value: localForm.dateOfHire,
                    onChange: (e) => handleChange('dateOfHire')(e),
                    onBlur: updateIntakeStateAndSubmit((dateOfHire) =>
                      updateIntakeStateAtKey({ dateOfHire })
                    ),
                  })}
                </Grid>
              )}
              <Grid item xs={6} md={6} lg={4}>
                {renderMuiFormField({
                  autoFocus: false,
                  id: 'dateOfPlannedRetirement',
                  label: 'Planned Retirement Date',
                  inputType: 'date',
                  value: localForm.dateOfPlannedRetirement,
                  onChange: (e) => handleChange('dateOfPlannedRetirement')(e),
                  onBlur: updateIntakeStateAndSubmit(
                    (dateOfPlannedRetirement) =>
                      updateIntakeStateAtKey({ dateOfPlannedRetirement })
                  ),
                })}
              </Grid>
              <Grid item xs={4} md={3} lg={4}>
                <Box
                  display="flex"
                  flexDirection="column"
                  justifyContent="flex-end"
                  height="100%"
                >
                  {renderMuiFormField({
                    autoFocus: false,
                    disabled: true,

                    label: 'CYS',
                    inputType: 'text',
                    value: Number(
                      (creditableYearsOfServiceToday || 0).toFixed(2)
                    ),
                  })}
                </Box>
              </Grid>
              <Grid item xs={4} md={3} xl={4}>
                <Box
                  display="flex"
                  flexDirection="column"
                  justifyContent="flex-end"
                  height="100%"
                >
                  {renderMuiFormField({
                    autoFocus: false,
                    disabled: true,

                    label: 'CYS @ Retirement',
                    inputType: 'text',
                    value: Number(
                      (creditableYearsOfServiceAtRetirement || 0).toFixed(2)
                    ),
                  })}
                </Box>
              </Grid>
              <Grid item xs={4} md={3} xl={4}>
                <Box
                  display="flex"
                  flexDirection="column"
                  justifyContent="flex-end"
                  height="100%"
                >
                  {renderMuiFormField({
                    autoFocus: false,
                    disabled: true,

                    label: 'Ret. Age',
                    inputType: 'text',
                    value: dateToYears(
                      intakeState.dateOfBirth,
                      intakeState.dateOfPlannedRetirement
                    ).years,
                  })}
                </Box>
              </Grid>
              {isGovernmentEmployee && (
                <>
                  <Grid item xs={6} md={6} xl={4}>
                    {renderMuiFormField({
                      autoFocus: false,
                      id: 'retirementSystem',
                      label: 'Retirement System',
                      placeholder: '',
                      inputType: 'select',
                      value: intakeState.retirementSystem,
                      onChange: updateIntakeStateAndSubmit((retirementSystem) =>
                        updateIntakeStateAtKey({ retirementSystem })
                      ),
                      options: selectData.retirementSystem,
                    })}
                  </Grid>
                  <Grid item xs={6} md={6} xl={4}>
                    {renderMuiFormField({
                      autoFocus: false,
                      id: 'retirementType',
                      label: 'Retirement Type',
                      placeholder: 'Retirement Type',
                      inputType: 'select',
                      value: intakeState.retirementType,
                      onChange: updateIntakeStateAndSubmit((retirementType) =>
                        updateIntakeStateAtKey({ retirementType })
                      ),
                      options: selectData.retirementType,
                    })}
                  </Grid>
                </>
              )}
            </Grid>
          </Collapse>
        </Card>
      </Grid>
    </>
  )
}

export default Employment
